import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import "./AddCalculationRule.css";
import { API_BASE } from "../../../ApiUrl";
import UserTabs from "../UserRegistrations/UserTabs";

const AddCalculationRule = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // The rule passed from CalculationRule via navigate(..., { state: { editRule } })
  const editRuleFromState = location.state?.editRule || null;
  const editRuleId = editRuleFromState?.id ?? null;
  const isEditMode = Boolean(editRuleId);

  const [formData, setFormData] = useState({
    calculation_field_code: "",
    scope: "",
    sequence: "",
    label: "",
    match_type: "ALL_CONDITIONS",
    min_calendar_experience_months: "",
    max_calendar_experience_months: "",
    min_verified_field_days: "",
    max_verified_field_days: "",
    min_verified_project_count: "",
    max_verified_project_count: "",
    min_qualion_level: "",
    max_qualion_level: "",
    min_authority_status: "",
    min_complexity_rating: "",
    min_ethics_independence_score: "",
    require_latest_assessment_decision: "",
    required_credential_types: [],
    require_active_credential: false,
    max_days_to_credential_expiry: "",
    block_if_pending_rejection: false,
    concluded_qualion_level: "",
    concluded_deployability_status: "",
    concluded_classification: "",
    requires_four_eyes_approval: false,
    is_active: true
  });

  const [credentialOpen, setCredentialOpen] = useState(false);

  // Submission state
  const [submitting, setSubmitting] = useState(false);

  // Edit fetch state
  const [ruleLoading, setRuleLoading] = useState(false);

  // Scope catalog state
  const [scopeOptions, setScopeOptions] = useState([]);
  const [scopeLoading, setScopeLoading] = useState(false);
  const [scopeError, setScopeError] = useState("");

  // Reference values state
  const [referenceValues, setReferenceValues] = useState([]);
  const [referenceLoading, setReferenceLoading] = useState(false);
  const [referenceError, setReferenceError] = useState("");

  // ---------------------------------------------------------------------------
  // SweetAlert helpers
  // ---------------------------------------------------------------------------
  const showSuccessAlert = (message, then) => {
    Swal.fire({
      icon: "success",
      title: "Success",
      text: message,
      confirmButtonColor: "#3085d6",
      timer: 1800,
      timerProgressBar: true,
      showConfirmButton: false
    }).then(() => {
      if (typeof then === "function") then();
    });
  };

  const showErrorAlert = (message, title = "Error") => {
    Swal.fire({
      icon: "error",
      title,
      text: message || "Something went wrong.",
      confirmButtonColor: "#d33"
    });
  };

  // ---------------------------------------------------------------------------
  // Fetch scope catalogs
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const fetchScopeCatalogs = async () => {
      setScopeLoading(true);
      setScopeError("");

      try {
        const response = await fetch(`${API_BASE}/scope-catalogs/`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        if (result.success && Array.isArray(result.data)) {
          setScopeOptions(result.data);
        } else {
          setScopeOptions([]);
        }
      } catch (error) {
        console.error("Error fetching scope catalogs:", error);
        setScopeError("Failed to load scope catalogs.");
        setScopeOptions([]);
      } finally {
        setScopeLoading(false);
      }
    };

    fetchScopeCatalogs();
  }, []);

  // ---------------------------------------------------------------------------
  // Fetch reference values
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const fetchReferenceValues = async () => {
      setReferenceLoading(true);
      setReferenceError("");

      try {
        const response = await fetch(`${API_BASE}/reference-values/`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        if (result.success && Array.isArray(result.data)) {
          setReferenceValues(result.data);
        } else {
          setReferenceValues([]);
        }
      } catch (error) {
        console.error("Error fetching reference values:", error);
        setReferenceError("Failed to load reference values.");
        setReferenceValues([]);
      } finally {
        setReferenceLoading(false);
      }
    };

    fetchReferenceValues();
  }, []);

  // ---------------------------------------------------------------------------
  // Fetch existing rule by ID (edit mode)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!editRuleId) return;

    const fetchRuleById = async () => {
      setRuleLoading(true);

      try {
        const response = await fetch(
          `${API_BASE}/calculation-rules/${editRuleId}/`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        const rule = result?.data ?? result;

        if (!rule) {
          throw new Error("Rule not found.");
        }

        setFormData((prev) => ({
          ...prev,
          calculation_field_code: rule.calculation_field_code ?? "",
          sequence: rule.sequence ?? "",
          label: rule.label ?? "",
          match_type: rule.match_type ?? "ALL_CONDITIONS",
          min_calendar_experience_months:
            rule.min_calendar_experience_months ?? "",
          max_calendar_experience_months:
            rule.max_calendar_experience_months ?? "",
          min_verified_field_days: rule.min_verified_field_days ?? "",
          max_verified_field_days: rule.max_verified_field_days ?? "",
          min_verified_project_count:
            rule.min_verified_project_count ?? "",
          max_verified_project_count:
            rule.max_verified_project_count ?? "",
          min_ethics_independence_score:
            rule.min_ethics_independence_score ?? "",
          require_latest_assessment_decision:
            rule.require_latest_assessment_decision ?? "",
          require_active_credential: !!rule.require_active_credential,
          max_days_to_credential_expiry:
            rule.max_days_to_credential_expiry ?? "",
          block_if_pending_rejection: !!rule.block_if_pending_rejection,
          concluded_deployability_status:
            rule.concluded_deployability_status ?? "",
          concluded_classification: rule.concluded_classification ?? "",
          requires_four_eyes_approval: !!rule.requires_four_eyes_approval,
          is_active: rule.is_active ?? true
        }));

        rawRuleRef.current = rule;
        setRawRule(rule);
      } catch (error) {
        console.error("Error fetching rule by id:", error);
        showErrorAlert(
          error.message || "Failed to load calculation rule.",
          "Load Failed"
        );
        // Navigate back to the listing since we can't edit an unknown rule
        setTimeout(() => navigate("/admin/calculation-rules"), 1500);
      } finally {
        setRuleLoading(false);
      }
    };

    fetchRuleById();
  }, [editRuleId, navigate]);

  // ---------------------------------------------------------------------------
  // Resolve raw rule IDs -> codes once scopeOptions/referenceValues are loaded
  // ---------------------------------------------------------------------------
  const [rawRule, setRawRule] = useState(null);
  const rawRuleRef = React.useRef(null);

  useEffect(() => {
    const rule = rawRule || rawRuleRef.current;
    if (!rule) return;

    const refIdToCode = (id) => {
      if (id === null || id === undefined) return "";
      const match = referenceValues.find((r) => r.id === id);
      return match ? match.code : "";
    };

    const scopeIdToCode = (ids) => {
      if (!Array.isArray(ids) || ids.length === 0) return "";
      const match = scopeOptions.find((s) => s.id === ids[0]);
      return match ? match.code : "";
    };

    const credentialIdsToCodes = (ids) => {
      if (!Array.isArray(ids)) return [];
      return ids
        .map((id) => referenceValues.find((r) => r.id === id)?.code)
        .filter(Boolean);
    };

    setFormData((prev) => ({
      ...prev,
      scope: scopeIdToCode(rule.scope),
      min_qualion_level: refIdToCode(rule.min_qualion_level),
      max_qualion_level: refIdToCode(rule.max_qualion_level),
      min_authority_status: refIdToCode(rule.min_authority_status),
      min_complexity_rating: refIdToCode(rule.min_complexity_rating),
      concluded_qualion_level: refIdToCode(rule.concluded_qualion_level),
      required_credential_types: credentialIdsToCodes(
        rule.required_credential_types
      )
    }));
  }, [rawRule, scopeOptions, referenceValues]);

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------
  const getOptionsByType = (optionType) =>
    referenceValues.filter(
      (item) => item.option_set_value?.option_type === optionType
    );

  const qualionLevelOptions = getOptionsByType("QUALION_LEVEL");

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleCredentialChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      required_credential_types: prev.required_credential_types.includes(value)
        ? prev.required_credential_types.filter((item) => item !== value)
        : [...prev.required_credential_types, value]
    }));
  };

  const handleSelectAllCredentials = () => {
    setFormData((prev) => ({
      ...prev,
      required_credential_types:
        prev.required_credential_types.length === credentialOptions.length
          ? []
          : credentialOptions.map((option) => option.value)
    }));
  };

  const credentialOptions = getOptionsByType("QUALION_LEVEL").map((item) => ({
    value: item.code,
    label: item.label
  }));

  const getCredentialLabel = () => {
    const selected = formData.required_credential_types;
    if (selected.length === 0) return "Select credential types";
    if (selected.length === credentialOptions.length)
      return "All credential types";
    return selected
      .map(
        (value) =>
          credentialOptions.find((option) => option.value === value)?.label
      )
      .filter(Boolean)
      .join(", ");
  };

  const toInt = (value, fallback = 0) => {
    if (value === "" || value === null || value === undefined) return fallback;
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? fallback : parsed;
  };

  const toFloat = (value, fallback = 0) => {
    if (value === "" || value === null || value === undefined) return fallback;
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? fallback : parsed;
  };

  const resolveReferenceId = (code) => {
    if (code === "" || code === null || code === undefined) return null;
    const match = referenceValues.find(
      (r) => String(r.code) === String(code)
    );
    return match ? match.id : null;
  };

  const getTenantId = () => {
    try {
      const raw = localStorage.getItem("tenant_user");
      if (!raw) return 1;
      const parsed = JSON.parse(raw);
      return parsed?.tenant_id ?? 1;
    } catch (err) {
      console.warn("Failed to parse tenant_user from localStorage:", err);
      return 1;
    }
  };

  const buildPayload = () => {
    const selectedScopeId = formData.scope
      ? (() => {
          const match = scopeOptions.find(
            (s) => String(s.code) === String(formData.scope)
          );
          return match ? match.id : toInt(formData.scope);
        })()
      : null;

    const credentialIds = formData.required_credential_types
      .map((code) => resolveReferenceId(code))
      .filter((id) => id !== null);

    const minQualionId = resolveReferenceId(formData.min_qualion_level);
    const maxQualionId = resolveReferenceId(formData.max_qualion_level);
    const minAuthorityId = resolveReferenceId(formData.min_authority_status);
    const minComplexityId = resolveReferenceId(formData.min_complexity_rating);
    const concludedQualionId = resolveReferenceId(
      formData.concluded_qualion_level
    );

    const MAX_SEQUENCE = 65535;
    const sequence = Math.min(toInt(formData.sequence, 0), MAX_SEQUENCE);

    const payload = {
      calculation_field_code: formData.calculation_field_code,
      sequence,
      label: formData.label,
      match_type: formData.match_type,
      min_calendar_experience_months: toInt(
        formData.min_calendar_experience_months,
        0
      ),
      max_calendar_experience_months: toInt(
        formData.max_calendar_experience_months,
        0
      ),
      min_verified_field_days: toFloat(formData.min_verified_field_days, 0),
      max_verified_field_days: toFloat(formData.max_verified_field_days, 0),
      min_verified_project_count: toInt(
        formData.min_verified_project_count,
        0
      ),
      max_verified_project_count: toInt(
        formData.max_verified_project_count,
        0
      ),
      min_ethics_independence_score: formData.min_ethics_independence_score
        ? String(formData.min_ethics_independence_score)
        : "0",
      require_latest_assessment_decision:
        formData.require_latest_assessment_decision || "",
      require_active_credential: !!formData.require_active_credential,
      max_days_to_credential_expiry: toInt(
        formData.max_days_to_credential_expiry,
        0
      ),
      block_if_pending_rejection: !!formData.block_if_pending_rejection,
      concluded_deployability_status:
        formData.concluded_deployability_status || "",
      concluded_classification: formData.concluded_classification || "",
      requires_four_eyes_approval: !!formData.requires_four_eyes_approval,
      is_active: !!formData.is_active,
      tenant: getTenantId(),
      scope: selectedScopeId !== null ? [selectedScopeId] : [],
      required_credential_types: credentialIds
    };

    if (minQualionId !== null) payload.min_qualion_level = minQualionId;
    if (maxQualionId !== null) payload.max_qualion_level = maxQualionId;
    if (minAuthorityId !== null) payload.min_authority_status = minAuthorityId;
    if (minComplexityId !== null)
      payload.min_complexity_rating = minComplexityId;
    if (concludedQualionId !== null)
      payload.concluded_qualion_level = concludedQualionId;

    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSubmitting(true);

    const payload = buildPayload();
    const url = isEditMode
      ? `${API_BASE}/calculation-rules/${editRuleId}/`
      : `${API_BASE}/calculation-rules/`;
    const method = isEditMode ? "PUT" : "POST";

    console.log(
      `${isEditMode ? "Updating" : "Creating"} Calculation Rule payload:`,
      payload
    );

    try {
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        let message = "";
        if (data?.errors && typeof data.errors === "object") {
          message = Object.entries(data.errors)
            .map(
              ([field, errs]) =>
                `${field}: ${Array.isArray(errs) ? errs.join(", ") : errs}`
            )
            .join(" | ");
        } else {
          message =
            data?.message ||
            data?.detail ||
            `Request failed with status ${response.status}`;
        }
        throw new Error(message);
      }

      console.log("Save rule response:", data);

      showSuccessAlert(
        isEditMode
          ? "Calculation rule updated successfully."
          : "Calculation rule created successfully.",
        () => navigate("/admin/calculation-rules")
      );
    } catch (error) {
      console.error("Error saving calculation rule:", error);
      showErrorAlert(
        error.message || "Failed to save calculation rule.",
        "Save Failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/admin/calculation-rules");
  };

  const isQualion = formData.calculation_field_code === "QUALION_LEVEL";
  const isDeployability =
    formData.calculation_field_code === "DEPLOYABILITY_FLAG";
  const isClassification =
    formData.calculation_field_code === "CANDIDATE_MENTOR_CLASSIFICATION";

  // While the edit-mode rule is loading, show a placeholder
  if (isEditMode && ruleLoading) {
    return (
      <div className="app-layout">
        <Sidebar />
        <div className="main-content">
          <Header />
          <div className="add-calculation-rule-page">
            <div style={{ padding: "40px", textAlign: "center" }}>
              Loading calculation rule...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="add-calculation-rule-page">
          <UserTabs/>
          <div className="add-calculation-rule-header">
            <h2>
              {isEditMode ? "Edit Calculation Rule" : "Create Calculation Rule"}
            </h2>

            <button
              type="button"
              className="add-calculation-back-btn"
              onClick={handleCancel}
            >
              ← Back
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            {/* BASIC INFORMATION */}
            <div className="add-calculation-rule-card">
              <div className="add-calculation-card-title">
                <h3>Basic Information</h3>
              </div>

              <div className="add-calculation-form-grid add-calculation-four-columns">
                <div className="add-calculation-form-group">
                  <label>
                    Calculation Field <span>*</span>
                  </label>
                  <select
                    name="calculation_field_code"
                    value={formData.calculation_field_code}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select calculation field</option>
                    <option value="QUALION_LEVEL">Qualion Level</option>
                    <option value="DEPLOYABILITY_FLAG">
                      Deployability Flag
                    </option>
                    <option value="CANDIDATE_MENTOR_CLASSIFICATION">
                      Candidate / Mentor Classification
                    </option>
                  </select>
                </div>

                <div className="add-calculation-form-group">
                  <label>Scope</label>
                  <select
                    name="scope"
                    value={formData.scope}
                    onChange={handleChange}
                    disabled={scopeLoading}
                  >
                    <option value="">
                      {scopeLoading
                        ? "Loading scopes..."
                        : scopeError
                        ? "Failed to load scopes"
                        : "Select Scope"}
                    </option>

                    {!scopeLoading &&
                      !scopeError &&
                      scopeOptions.map((scope) => (
                        <option key={scope.id} value={scope.code}>
                          {scope.code} — {scope.scope_name}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="add-calculation-form-group">
                  <label>
                    Sequence <span>*</span>
                  </label>
                  <input
                    type="number"
                    name="sequence"
                    min="1"
                    max="65535"
                    value={formData.sequence}
                    onChange={handleChange}
                    placeholder="Enter sequence"
                    required
                  />
                </div>

                <div className="add-calculation-form-group">
                  <label>
                    Rule Label <span>*</span>
                  </label>
                  <input
                    type="text"
                    name="label"
                    value={formData.label}
                    onChange={handleChange}
                    placeholder="Enter rule description"
                    required
                  />
                </div>
              </div>

              <div className="add-calculation-match-type-row">
                <label className="add-calculation-match-label">
                  Match Type <span>*</span>
                </label>

                <label className="add-calculation-radio-option">
                  <input
                    type="radio"
                    name="match_type"
                    value="ALL_CONDITIONS"
                    checked={formData.match_type === "ALL_CONDITIONS"}
                    onChange={handleChange}
                  />
                  <span>All conditions must be met</span>
                </label>

                <label className="add-calculation-radio-option">
                  <input
                    type="radio"
                    name="match_type"
                    value="ANY_CONDITION"
                    checked={formData.match_type === "ANY_CONDITION"}
                    onChange={handleChange}
                  />
                  <span>Any one condition is sufficient</span>
                </label>
              </div>
            </div>

            {/* CONDITIONS */}
            <div className="add-calculation-rule-card">
              <div className="add-calculation-card-title">
                <h3>Conditions</h3>
              </div>

              {isQualion && (
                <div className="add-calculation-form-grid add-calculation-four-columns">
                  <div className="add-calculation-form-group">
                    <label>Min. Calendar Experience</label>
                    <div className="add-calculation-input-with-unit">
                      <input
                        type="number"
                        name="min_calendar_experience_months"
                        value={formData.min_calendar_experience_months}
                        onChange={handleChange}
                        placeholder="0"
                        min="0"
                      />
                      <span>months</span>
                    </div>
                  </div>

                  <div className="add-calculation-form-group">
                    <label>Max. Calendar Experience</label>
                    <div className="add-calculation-input-with-unit">
                      <input
                        type="number"
                        name="max_calendar_experience_months"
                        value={formData.max_calendar_experience_months}
                        onChange={handleChange}
                        placeholder="0"
                        min="0"
                      />
                      <span>months</span>
                    </div>
                  </div>

                  <div className="add-calculation-form-group">
                    <label>Min. Verified Field Days</label>
                    <div className="add-calculation-input-with-unit">
                      <input
                        type="number"
                        step="0.01"
                        name="min_verified_field_days"
                        value={formData.min_verified_field_days}
                        onChange={handleChange}
                        placeholder="0.00"
                        min="0"
                      />
                      <span>days</span>
                    </div>
                  </div>

                  <div className="add-calculation-form-group">
                    <label>Max. Verified Field Days</label>
                    <div className="add-calculation-input-with-unit">
                      <input
                        type="number"
                        step="0.01"
                        name="max_verified_field_days"
                        value={formData.max_verified_field_days}
                        onChange={handleChange}
                        placeholder="0.00"
                        min="0"
                      />
                      <span>days</span>
                    </div>
                  </div>

                  <div className="add-calculation-form-group">
                    <label>Min. Verified Projects</label>
                    <div className="add-calculation-input-with-unit">
                      <input
                        type="number"
                        name="min_verified_project_count"
                        value={formData.min_verified_project_count}
                        onChange={handleChange}
                        placeholder="0"
                        min="0"
                      />
                      <span>projects</span>
                    </div>
                  </div>

                  <div className="add-calculation-form-group">
                    <label>Max. Verified Projects</label>
                    <div className="add-calculation-input-with-unit">
                      <input
                        type="number"
                        name="max_verified_project_count"
                        value={formData.max_verified_project_count}
                        onChange={handleChange}
                        placeholder="0"
                        min="0"
                      />
                      <span>projects</span>
                    </div>
                  </div>

                  <div className="add-calculation-form-group">
                    <label>Min. Authority Status</label>
                    <select
                      name="min_authority_status"
                      value={formData.min_authority_status}
                      onChange={handleChange}
                      disabled={referenceLoading}
                    >
                      <option value="">
                        {referenceLoading ? "Loading..." : "Select status"}
                      </option>
                      {qualionLevelOptions.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.code} — {item.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="add-calculation-form-group">
                    <label>Min. Complexity Rating</label>
                    <select
                      name="min_complexity_rating"
                      value={formData.min_complexity_rating}
                      onChange={handleChange}
                      disabled={referenceLoading}
                    >
                      <option value="">
                        {referenceLoading ? "Loading..." : "Select rating"}
                      </option>
                      {qualionLevelOptions.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.code} — {item.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="add-calculation-form-group add-calculation-multiselect-group">
                    <label>Required Credential Types</label>

                    <div className="add-calculation-multiselect">
                      <button
                        type="button"
                        className={`add-calculation-multiselect-trigger ${
                          credentialOpen ? "add-calculation-open" : ""
                        }`}
                        onClick={() => setCredentialOpen(!credentialOpen)}
                        disabled={referenceLoading}
                      >
                        <span
                          className={
                            formData.required_credential_types.length === 0
                              ? "add-calculation-placeholder"
                              : ""
                          }
                        >
                          {referenceLoading
                            ? "Loading..."
                            : getCredentialLabel()}
                        </span>
                        <span className="add-calculation-dropdown-arrow">
                          ▼
                        </span>
                      </button>

                      {credentialOpen && (
                        <div className="add-calculation-multiselect-menu">
                          <label className="add-calculation-checkbox-option add-calculation-select-all">
                            <input
                              type="checkbox"
                              checked={
                                formData.required_credential_types.length ===
                                credentialOptions.length
                              }
                              onChange={handleSelectAllCredentials}
                            />
                            <span>Select All</span>
                          </label>

                          {credentialOptions.map((option) => (
                            <label
                              key={option.value}
                              className="add-calculation-checkbox-option"
                            >
                              <input
                                type="checkbox"
                                checked={formData.required_credential_types.includes(
                                  option.value
                                )}
                                onChange={() =>
                                  handleCredentialChange(option.value)
                                }
                              />
                              <span>{option.label}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="add-calculation-toggle-field">
                    <div>
                      <label>Require Active Credential</label>
                    </div>

                    <label className="add-calculation-switch">
                      <input
                        type="checkbox"
                        name="require_active_credential"
                        checked={formData.require_active_credential}
                        onChange={handleChange}
                      />
                      <span className="add-calculation-slider"></span>
                    </label>
                  </div>
                </div>
              )}

              {isDeployability && (
                <div className="add-calculation-form-grid add-calculation-four-columns">
                  <div className="add-calculation-form-group">
                    <label>Min. Qualion Level</label>
                    <select
                      name="min_qualion_level"
                      value={formData.min_qualion_level}
                      onChange={handleChange}
                      disabled={referenceLoading}
                    >
                      <option value="">
                        {referenceLoading ? "Loading..." : "Select level"}
                      </option>
                      {qualionLevelOptions.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.code} — {item.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="add-calculation-form-group">
                    <label>Max. Qualion Level</label>
                    <select
                      name="max_qualion_level"
                      value={formData.max_qualion_level}
                      onChange={handleChange}
                      disabled={referenceLoading}
                    >
                      <option value="">
                        {referenceLoading ? "Loading..." : "Select level"}
                      </option>
                      {qualionLevelOptions.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.code} — {item.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="add-calculation-form-group">
                    <label>Min. Authority Status</label>
                    <select
                      name="min_authority_status"
                      value={formData.min_authority_status}
                      onChange={handleChange}
                      disabled={referenceLoading}
                    >
                      <option value="">
                        {referenceLoading ? "Loading..." : "Select status"}
                      </option>
                      {qualionLevelOptions.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.code} — {item.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="add-calculation-form-group add-calculation-multiselect-group">
                    <label>Required Credential Types</label>

                    <div className="add-calculation-multiselect">
                      <button
                        type="button"
                        className={`add-calculation-multiselect-trigger ${
                          credentialOpen ? "add-calculation-open" : ""
                        }`}
                        onClick={() => setCredentialOpen(!credentialOpen)}
                        disabled={referenceLoading}
                      >
                        <span
                          className={
                            formData.required_credential_types.length === 0
                              ? "add-calculation-placeholder"
                              : ""
                          }
                        >
                          {referenceLoading
                            ? "Loading..."
                            : getCredentialLabel()}
                        </span>
                        <span className="add-calculation-dropdown-arrow">
                          ▼
                        </span>
                      </button>

                      {credentialOpen && (
                        <div className="add-calculation-multiselect-menu">
                          <label className="add-calculation-checkbox-option add-calculation-select-all">
                            <input
                              type="checkbox"
                              checked={
                                formData.required_credential_types.length ===
                                credentialOptions.length
                              }
                              onChange={handleSelectAllCredentials}
                            />
                            <span>Select All</span>
                          </label>

                          {credentialOptions.map((option) => (
                            <label
                              key={option.value}
                              className="add-calculation-checkbox-option"
                            >
                              <input
                                type="checkbox"
                                checked={formData.required_credential_types.includes(
                                  option.value
                                )}
                                onChange={() =>
                                  handleCredentialChange(option.value)
                                }
                              />
                              <span>{option.label}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="add-calculation-form-group">
                    <label>Max. Days to Credential Expiry</label>
                    <div className="add-calculation-input-with-unit">
                      <input
                        type="number"
                        name="max_days_to_credential_expiry"
                        value={formData.max_days_to_credential_expiry}
                        onChange={handleChange}
                        placeholder="30"
                        min="0"
                      />
                      <span>days</span>
                    </div>
                  </div>

                  <div className="add-calculation-toggle-field">
                    <div>
                      <label>Require Active Credential</label>
                    </div>

                    <label className="add-calculation-switch">
                      <input
                        type="checkbox"
                        name="require_active_credential"
                        checked={formData.require_active_credential}
                        onChange={handleChange}
                      />
                      <span className="add-calculation-slider"></span>
                    </label>
                  </div>
                </div>
              )}

              {isClassification && (
                <div className="add-calculation-form-grid add-calculation-four-columns">
                  <div className="add-calculation-form-group">
                    <label>Min. Qualion Level</label>
                    <select
                      name="min_qualion_level"
                      value={formData.min_qualion_level}
                      onChange={handleChange}
                      disabled={referenceLoading}
                    >
                      <option value="">
                        {referenceLoading ? "Loading..." : "Select level"}
                      </option>
                      {qualionLevelOptions.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.code} — {item.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="add-calculation-form-group">
                    <label>Max. Qualion Level</label>
                    <select
                      name="max_qualion_level"
                      value={formData.max_qualion_level}
                      onChange={handleChange}
                      disabled={referenceLoading}
                    >
                      <option value="">
                        {referenceLoading ? "Loading..." : "Select level"}
                      </option>
                      {qualionLevelOptions.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.code} — {item.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="add-calculation-form-group">
                    <label>Min. Authority Status</label>
                    <select
                      name="min_authority_status"
                      value={formData.min_authority_status}
                      onChange={handleChange}
                      disabled={referenceLoading}
                    >
                      <option value="">
                        {referenceLoading ? "Loading..." : "Select status"}
                      </option>
                      {qualionLevelOptions.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.code} — {item.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="add-calculation-form-group">
                    <label>Min. Ethics & Independence Score</label>
                    <div className="add-calculation-input-with-unit">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        name="min_ethics_independence_score"
                        value={formData.min_ethics_independence_score}
                        onChange={handleChange}
                        placeholder="0.00"
                      />
                      <span>/ 100</span>
                    </div>
                  </div>

                  <div className="add-calculation-form-group">
                    <label>Latest Assessment Decision</label>
                    <select
                      name="require_latest_assessment_decision"
                      value={formData.require_latest_assessment_decision}
                      onChange={handleChange}
                    >
                      <option value="">Not checked</option>
                      <option value="DRAFT">Draft</option>
                      <option value="SUBMITTED">Submitted</option>
                      <option value="APPROVED">Approved</option>
                      <option value="REJECTED">Rejected</option>
                    </select>
                  </div>

                  <div className="add-calculation-form-group add-calculation-multiselect-group">
                    <label>Required Credential Types</label>

                    <div className="add-calculation-multiselect">
                      <button
                        type="button"
                        className={`add-calculation-multiselect-trigger ${
                          credentialOpen ? "add-calculation-open" : ""
                        }`}
                        onClick={() => setCredentialOpen(!credentialOpen)}
                        disabled={referenceLoading}
                      >
                        <span
                          className={
                            formData.required_credential_types.length === 0
                              ? "add-calculation-placeholder"
                              : ""
                          }
                        >
                          {referenceLoading
                            ? "Loading..."
                            : getCredentialLabel()}
                        </span>
                        <span className="add-calculation-dropdown-arrow">
                          ▼
                        </span>
                      </button>

                      {credentialOpen && (
                        <div className="add-calculation-multiselect-menu">
                          <label className="add-calculation-checkbox-option add-calculation-select-all">
                            <input
                              type="checkbox"
                              checked={
                                formData.required_credential_types.length ===
                                credentialOptions.length
                              }
                              onChange={handleSelectAllCredentials}
                            />
                            <span>Select All</span>
                          </label>

                          {credentialOptions.map((option) => (
                            <label
                              key={option.value}
                              className="add-calculation-checkbox-option"
                            >
                              <input
                                type="checkbox"
                                checked={formData.required_credential_types.includes(
                                  option.value
                                )}
                                onChange={() =>
                                  handleCredentialChange(option.value)
                                }
                              />
                              <span>{option.label}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="add-calculation-toggle-field">
                    <div>
                      <label>Block if Pending Rejection</label>
                    </div>

                    <label className="add-calculation-switch">
                      <input
                        type="checkbox"
                        name="block_if_pending_rejection"
                        checked={formData.block_if_pending_rejection}
                        onChange={handleChange}
                      />
                      <span className="add-calculation-slider"></span>
                    </label>
                  </div>
                </div>
              )}

              {!formData.calculation_field_code && (
                <div className="add-calculation-empty-condition">
                  Select a calculation field above to configure conditions.
                </div>
              )}
            </div>

            {/* OUTCOME */}
            {formData.calculation_field_code && (
              <div className="add-calculation-rule-card">
                <div className="add-calculation-card-title">
                  <h3>Rule Outcome</h3>
                </div>

                <div className="add-calculation-form-grid add-calculation-four-columns">
                  {isQualion && (
                    <div className="add-calculation-form-group">
                      <label>
                        Concluded Qualion Level <span>*</span>
                      </label>
                      <select
                        name="concluded_qualion_level"
                        value={formData.concluded_qualion_level}
                        onChange={handleChange}
                        required
                        disabled={referenceLoading}
                      >
                        <option value="">
                          {referenceLoading ? "Loading..." : "Select level"}
                        </option>
                        {qualionLevelOptions.map((item) => (
                          <option key={item.id} value={item.code}>
                            {item.code} — {item.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {isDeployability && (
                    <div className="add-calculation-form-group">
                      <label>
                        Concluded Deployability Status <span>*</span>
                      </label>
                      <select
                        name="concluded_deployability_status"
                        value={formData.concluded_deployability_status}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select status</option>
                        <option value="DEPLOYABLE">Deployable</option>
                        <option value="DEPLOYABLE_WITH_RESTRICTIONS">
                          Deployable with restrictions
                        </option>
                        <option value="REVIEW_REQUIRED">Review required</option>
                        <option value="NOT_DEPLOYABLE">Not deployable</option>
                      </select>
                    </div>
                  )}

                  {isClassification && (
                    <div className="add-calculation-form-group">
                      <label>
                        Concluded Classification <span>*</span>
                      </label>
                      <select
                        name="concluded_classification"
                        value={formData.concluded_classification}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select classification</option>
                        <option value="CANDIDATE">Candidate</option>
                        <option value="MENTOR">Mentor</option>
                        <option value="BOTH">Both</option>
                        <option value="UNCLASSIFIED">Unclassified</option>
                      </select>
                    </div>
                  )}

                  <div className="add-calculation-toggle-field">
                    <div>
                      <label>Admin Approval</label>
                    </div>

                    <label className="add-calculation-switch">
                      <input
                        type="checkbox"
                        name="requires_four_eyes_approval"
                        checked={formData.requires_four_eyes_approval}
                        onChange={handleChange}
                      />
                      <span className="add-calculation-slider"></span>
                    </label>
                  </div>

                  <div className="add-calculation-toggle-field">
                    <div>
                      <label>Rule Status</label>
                    </div>

                    <label className="add-calculation-switch">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleChange}
                      />
                      <span className="add-calculation-slider"></span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* RULE PREVIEW */}
            {formData.calculation_field_code && (
              <div className="add-calculation-rule-preview">
                <div className="add-calculation-preview-icon">✓</div>

                <div>
                  <strong>Rule Preview</strong>
                  <p>
                    {formData.label || "This rule"} will be evaluated at
                    sequence {formData.sequence || "—"} using{" "}
                    {formData.match_type === "ALL_CONDITIONS"
                      ? "all conditions"
                      : "any matching condition"}
                    .
                  </p>
                </div>
              </div>
            )}

            {/* ACTIONS */}
            <div className="add-calculation-form-actions">
              <button
                type="button"
                className="add-calculation-cancel-btn"
                onClick={handleCancel}
                disabled={submitting}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="add-calculation-save-btn"
                disabled={submitting}
              >
                {submitting
                  ? "Saving..."
                  : isEditMode
                  ? "Update Calculation Rule"
                  : "Save Calculation Rule"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddCalculationRule;